import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { TiposSolicitudService, TipoSolicitudDTO } from './tipos-solicitud.service';

/**
 * Resuelve el `idTipoSolicitud` de «Novedad de Traslado» vía `GET tipos-solicitud`
 * (no usar id quemado por ambiente). Ver `front-consumo-tipos-solicitud.md`.
 * El resultado se cachea en memoria por instancia (sesión).
 */
@Injectable({
  providedIn: 'root',
})
export class TipoSolicitudNovedadTrasladoIdService {
  private cached$: Observable<number> | null = null;

  constructor(private tiposSolicitudService: TiposSolicitudService) {}

  /** Invalida caché (p. ej. tras cambio de usuario o reintento explícito). */
  invalidateCache(): void {
    this.cached$ = null;
  }

  /**
   * Id numérico a usar en `tipos-novedad` / `subtipos-novedad` como query `idTipoSolicitud`.
   */
  getIdTipoSolicitudNovedadTraslado(): Observable<number> {
    if (!this.cached$) {
      this.cached$ = this.tiposSolicitudService.getTiposSolicitud().pipe(
        map((items) => this.pickId(items)),
        catchError((err) => {
          console.warn(
            '[TipoSolicitudNovedadTrasladoId] GET tipos-solicitud falló; se usa fallback de environment.',
            err
          );
          return of(environment.ID_TIPO_SOLICITUD_NOVEDAD_TRASLADO_FALLBACK);
        }),
        shareReplay(1)
      );
    }
    return this.cached$;
  }

  private normalizarTexto(s: string): string {
    return String(s ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private pickId(items: TipoSolicitudDTO[] | null | undefined): number {
    const list = Array.isArray(items) ? items : [];
    const activos = list.filter(
      (x) => x && (x.estado === 1 || x.estado === '1' || x.estado == null)
    );
    const fuente = activos.length > 0 ? activos : list;

    const descObjetivo = this.normalizarTexto(
      environment.TIPO_SOLICITUD_NOVEDAD_TRASLADO_DESCRIPCION ??
        'Novedad de Traslado'
    );
    const codObjetivo = String(
      environment.TIPO_SOLICITUD_NOVEDAD_TRASLADO_CODIGO ?? ''
    )
      .trim()
      .toUpperCase();

    for (const x of fuente) {
      const cod = String(x.codigo ?? '')
        .trim()
        .toUpperCase();
      if (codObjetivo && cod === codObjetivo) {
        return Number(x.idTipoSolicitud);
      }
    }

    for (const x of fuente) {
      if (this.normalizarTexto(x.descripcion ?? '') === descObjetivo) {
        return Number(x.idTipoSolicitud);
      }
    }

    const suelto = fuente.find((x) => {
      const d = this.normalizarTexto(x.descripcion ?? '');
      return d.includes('novedad') && d.includes('traslado');
    });
    if (suelto) {
      return Number(suelto.idTipoSolicitud);
    }

    console.warn(
      '[TipoSolicitudNovedadTrasladoId] No se encontró «Novedad de Traslado» en tipos-solicitud; fallback.'
    );
    return environment.ID_TIPO_SOLICITUD_NOVEDAD_TRASLADO_FALLBACK;
  }
}
